import {
  Dispatch,
  FC,
  ReactElement,
  Ref,
  SetStateAction,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogContent, DialogTitle, Slide, Stack } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import { AttachmentResponse } from '@/api/data-contracts';
import ActiveFile from '@/features/media/ui/FileManager/ActiveFile';
import { Content, DialogFileTitle } from '@/features/media/ui/FileManager/FileDialog.styles';
import ImageNavigationButton from '@/features/media/ui/FileManager/ImageNavigationButton';
import Menu from '@/features/media/ui/FileManager/Menu';
import MenuContent from '@/features/media/ui/FileManager/MenuContent';
import ThumbnailsList from '@/features/media/ui/FileManager/ThumbnailsList';
import Toolbar from '@/features/media/ui/FileManager/Toolbar';

interface Props {
  orderId?: string;
  taskId?: string;
  bookingId?: string;
  mediaPlanId?: string;
  advertisingSpaceId?: string;
  files: File[];
  setFiles: Dispatch<SetStateAction<File[]>>;
  attachments?: AttachmentResponse[];
  isOpen: boolean;
  onClose: () => void;
  defaultImageIndex: number | undefined;
  disableVersionUpload?: boolean;
  category?: 'keyVisual' | 'artWork';
  thumbnails: File[];
}

const FileDialog: FC<Props> = ({
  orderId,
  taskId,
  bookingId,
  mediaPlanId,
  advertisingSpaceId,
  files,
  setFiles,
  attachments,
  isOpen,
  onClose,
  defaultImageIndex,
  disableVersionUpload,
  category,
  thumbnails,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [zoom, setZoom] = useState<number>(2);
  const [contentType, setContentType] = useState<
    'comments' | 'tasks' | 'versions' | 'info' | undefined
  >();
  const [activeFile, setActiveFile] = useState<File | undefined>();
  const [activeFileIndex, setActiveFileIndex] = useState<number>(defaultImageIndex || 0);
  const [commentsThreadId, setCommentsThreadId] = useState<string>();
  const [width, setWidth] = useState<number>(0);
  const [height] = useState<number>(512);

  useEffect(() => {
    setActiveFile(files?.[activeFileIndex]);
  }, [activeFileIndex, files]);

  const renderPrevImageButton = useCallback(
    () => (
      <ImageNavigationButton
        isPrev
        isDisabled={!activeFileIndex}
        onClick={() => {
          setZoom(1);
          setActiveFileIndex((current) => current - 1);
        }}
      />
    ),
    [activeFileIndex],
  );

  const renderNextImageButton = useCallback(
    () => (
      <ImageNavigationButton
        isDisabled={!files.length || activeFileIndex === files.length - 1}
        onClick={() => {
          setZoom(1);
          setActiveFileIndex((current) => current + 1);
        }}
      />
    ),
    [activeFileIndex, files.length],
  );

  return (
    <Dialog
      open={isOpen}
      maxWidth="xl"
      onClose={() => {
        setContentType(undefined);
        onClose();
      }}
      fullWidth
      TransitionComponent={Transition}
      sx={{
        '& .MuiPaper-root': {
          padding: 0,
        },
      }}
    >
      <DialogTitle sx={{ padding: 0, paddingLeft: '1rem' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <DialogFileTitle>{files?.[activeFileIndex]?.name || 'File'}</DialogFileTitle>

          <Button autoFocus color="inherit" onClick={onClose} sx={{ padding: '1rem' }}>
            <CloseIcon />
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          backgroundColor: (theme) => theme.palette.grey[50],
          padding: 0,
          position: 'relative',
        }}
      >
        <Stack direction="row">
          {renderPrevImageButton()}

          <Content ref={contentRef}>
            {!!activeFile && (
              <div key={activeFile.name}>
                <ActiveFile
                  zoom={zoom}
                  setZoom={setZoom}
                  width={width}
                  setWidth={setWidth}
                  height={height}
                  activeFile={activeFile}
                  taskId={taskId}
                  mediaPlanId={mediaPlanId}
                  attachmentId={attachments?.[activeFileIndex].id}
                  setCommentsThreadId={setCommentsThreadId}
                  onClickComment={() => {
                    setContentType('comments');
                  }}
                />
              </div>
            )}

            <ThumbnailsList
              files={thumbnails}
              activeFileIndex={activeFileIndex}
              onClick={(index: number) => {
                setZoom(1);
                setActiveFileIndex(index);
              }}
            />

            <Toolbar
              zoom={zoom}
              setZoom={setZoom}
              activeFile={activeFile}
              onClickComment={() => {
                setContentType('comments');
                setCommentsThreadId(undefined);
              }}
            />
          </Content>

          {renderNextImageButton()}

          {!!attachments?.[activeFileIndex] && !!activeFile && (
            <MenuContent
              contentType={contentType}
              setContentType={setContentType}
              activeFileIndex={activeFileIndex}
              files={files}
              setFiles={setFiles}
              setActiveFile={setActiveFile}
              attachment={attachments[activeFileIndex]}
              orderId={orderId}
              taskId={taskId}
              bookingId={bookingId}
              mediaPlanId={mediaPlanId}
              advertisingSpaceId={advertisingSpaceId}
              commentsThreadId={commentsThreadId}
              disableVersionUpload={disableVersionUpload}
              category={category}
            />
          )}

          <Menu
            contentType={contentType}
            setContentType={setContentType}
            setCommentsThreadId={setCommentsThreadId}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default FileDialog;

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement;
  },
  ref: Ref<unknown>,
) {
  // eslint-disable-next-line
  return <Slide direction="up" ref={ref} {...props} />;
});
